/**
 * @fileoverview GHS-8AT測定値の扱いと各ロギング条件の動作説明
 */
import Table from "../components/Table.tsx";
import { useTranslation } from "react-i18next";
import Ghs8atMeasureConcJ from "./img/Ghs8atMeasureConc.png";
import Ghs8atLogCon100J from "./img/Ghs8atLogCon100.png";
import Ghs8atMeasureConcE from "./img/English/Ghs8atMeasureConcE.png";
import Ghs8atLogCon100E from "./img/English/Ghs8atLogCon100E.png";
import Ghs8atMeasureConcK from "./img/Korean/Ghs8atMeasureConcK.png";
import Ghs8atLogCon100K from "./img/Korean/Ghs8atLogCon100K.png";

/**
 *
 * @returns GHS-8AT測定値の扱いと各ロギング条件の動作説明の JSX element
 */
export const Ghs8atMeasureConditions = () => {
  const { t, i18n } = useTranslation();

  // 画像の言語別のパス
  const images = {
    ja: {
      Ghs8atMeasureConc: Ghs8atMeasureConcJ,
      Ghs8atLogCon100: Ghs8atLogCon100J,
    },
    en: {
      Ghs8atMeasureConc: Ghs8atMeasureConcE,
      Ghs8atLogCon100: Ghs8atLogCon100E,
    },
    ko: {
      Ghs8atMeasureConc: Ghs8atMeasureConcK,
      Ghs8atLogCon100: Ghs8atLogCon100K,
    },
  };

  // 現在の言語に合わせて画像を切り替える
  const currentImages =
    i18n.language === "ja"
      ? images.ja
      : i18n.language === "ko"
      ? images.ko
      : images.en;

  /**
   * センサーフルスケールによる最小分解能のテーブルのヘッダー
   */
  const resolutionsHeader = [
    {
      ja: "フルスケール[ppm]",
      en: "Full scale [ppm]",
      ko: "풀 스케일 [ppm]",
    },
    { ja: "センサー型式", en: "Sensor model", ko: "센서 형식" },
    {
      ja: "最小分解能[ppm]",
      en: "Minimum resolution [ppm]",
      ko: "최소 분해능 [ppm]",
    },
  ];

  // 現在の言語に合わせてヘッダーを切り替える
  const translatedResolutionHeaders = resolutionsHeader.map((header) =>
    i18n.language === "ja"
      ? header.ja
      : i18n.language === "ko"
      ? header.ko
      : header.en
  );

  /**
   * センサーフルスケールによる最小分解能のテーブルのデータ
   */
  const resolutions = [
    {
      "フルスケール[ppm]": 10,
      "Full scale [ppm]": 10,
      "풀 스케일 [ppm]": 10,
      センサー型式: "H2S-520E",
      "Sensor model": "H2S-520E",
      "센서 형식": "H2S-520E",
      "最小分解能[ppm]": 0.1,
      "Minimum resolution [ppm]": 0.1,
      "최소 분해능 [ppm]": 0.1,
    },
    {
      "フルスケール[ppm]": 100,
      "Full scale [ppm]": 100,
      "풀 스케일 [ppm]": 100,
      センサー型式: "H2S-521E",
      "Sensor model": "H2S-521E",
      "센서 형식": "H2S-521E",
      "最小分解能[ppm]": 1,
      "Minimum resolution [ppm]": 1,
      "최소 분해능 [ppm]": 1,
    },
    {
      "フルスケール[ppm]": 500,
      "Full scale [ppm]": 500,
      "풀 스케일 [ppm]": 500,
      センサー型式: "H2S-522E",
      "Sensor model": "H2S-522E",
      "센서 형식": "H2S-522E",
      "最小分解能[ppm]": "1※",
      "Minimum resolution [ppm]": "1",
      "최소 분해능 [ppm]": "1",
    },
    {
      "フルスケール[ppm]": 1000,
      "Full scale [ppm]": 1000,
      "풀 스케일 [ppm]": 1000,
      センサー型式: "H2S-523E",
      "Sensor model": "H2S-523E",
      "센서 형식": "H2S-523E",
      "最小分解能[ppm]": 10,
      "Minimum resolution [ppm]": 10,
      "최소 분해능 [ppm]": 10,
    },
    {
      "フルスケール[ppm]": 3000,
      "Full scale [ppm]": 3000,
      "풀 스케일 [ppm]": 3000,
      センサー型式: "H2S-524",
      "Sensor model": "H2S-524",
      "센서 형식": "H2S-524",
      "最小分解能[ppm]": 25,
      "Minimum resolution [ppm]": 25,
      "최소 분해능 [ppm]": 25,
    },
  ];

  /**
   * 平均値計算時のデータの扱いテーブルのヘッダー
   */
  const averageTableHeader = [
    { ja: "フルスケール[ppm]", en: "Full scale [ppm]", ko: "풀 스케일 [ppm]" },
    { ja: "センサー型式", en: "Sensor model", ko: "센서 형식" },
    {
      ja: "平均値算出データ扱い",
      en: "Average value calculated data handling",
      ko: "평균값 산출 데이터",
    },
    {
      ja: "最小分解能[ppm]",
      en: "Minimum resolution [ppm]",
      ko: "최소 분해능 [ppm]",
    },
  ];

  // 現在の言語に合わせてヘッダーを切り替える
  const translatedAverageTableHeader = averageTableHeader.map((header) =>
    i18n.language === "ja"
      ? header.ja
      : i18n.language === "ko"
      ? header.ko
      : header.en
  );

  /**
   * 平均値計算時のデータの扱いテーブルのデータ
   */
  const averageTableData = [
    {
      "フルスケール[ppm]": 10,
      "Full scale [ppm]": 10,
      "풀 스케일 [ppm]": 10,
      センサー型式: "H2S-520E",
      "Sensor model": "H2S-520E",
      "센서 형식": "H2S-520E",
      平均値算出データ扱い: "小数点第2位以下切り捨て",
      "Average value calculated data handling":
        "Rounded down to the second decimal place",
      "평균값 산출 데이터": "소수점 2자리 이하 단수처리",
      "最小分解能[ppm]": 0.1,
      "Minimum resolution [ppm]": 0.1,
      "최소 분해능 [ppm]": 0.1,
    },
    {
      "フルスケール[ppm]": 100,
      "Full scale [ppm]": 100,
      "풀 스케일 [ppm]": 100,
      センサー型式: "H2S-521E",
      "Sensor model": "H2S-521E",
      "센서 형식": "H2S-521E",
      平均値算出データ扱い: "小数点以下切り捨て",
      "Average value calculated data handling": "Rounded down",
      "평균값 산출 데이터": "소수점 이하 단수처리",
      "最小分解能[ppm]": 1,
      "Minimum resolution [ppm]": 1,
      "최소 분해능 [ppm]": 1,
    },
    {
      "フルスケール[ppm]": 500,
      "Full scale [ppm]": 500,
      "풀 스케일 [ppm]": 500,
      センサー型式: "H2S-522E",
      "Sensor model": "H2S-522E",
      "센서 형식": "H2S-522E",
      平均値算出データ扱い: "小数点以下切り捨て",
      "Average value calculated data handling": "Rounded down",
      "평균값 산출 데이터": "소수점 이하 단수처리",
      "最小分解能[ppm]": 1,
      "Minimum resolution [ppm]": 1,
      "최소 분해능 [ppm]": 1,
    },
    {
      "フルスケール[ppm]": 1000,
      "Full scale [ppm]": 1000,
      "풀 스케일 [ppm]": 1000,
      センサー型式: "H2S-523E",
      "Sensor model": "H2S-523E",
      "센서 형식": "H2S-523E",
      平均値算出データ扱い: "小数点以下切り捨て",
      "Average value calculated data handling": "Rounded down",
      "평균값 산출 데이터": "소수점 이하 단수처리",
      "最小分解能[ppm]": 10,
      "Minimum resolution [ppm]": 10,
      "최소 분해능 [ppm]": 10,
    },
    {
      "フルスケール[ppm]": 3000,
      "Full scale [ppm]": 3000,
      "풀 스케일 [ppm]": 3000,
      センサー型式: "H2S-524",
      "Sensor model": "H2S-524",
      "센서 형식": "H2S-524",
      平均値算出データ扱い: "小数点以下切り捨て",
      "Average value calculated data handling": "Rounded down",
      "평균값 산출 데이터": "소수점 이하 단수처리",
      "最小分解能[ppm]": 25,
      "Minimum resolution [ppm]": 25,
      "최소 분해능 [ppm]": 25,
    },
  ];

  return (
    <section className="c-section">
      <h2 className="p-heading-2">
        {t("GHS-8AT測定値の扱いと各ロギング条件の動作説明")}
      </h2>

      <p className="c-text">
        {t("GHS-8ATの測定間隔, センサフルスケールによる測定値の扱い,") +
          t("ロギング条件によるロギング動作の違いについて説明します。")}
      </p>

      <h3 className="p-heading-3">{t("測定の間隔について")}</h3>
      <p className="c-text">
        {t(
          "測定とはガス濃度を取得して本体液晶表示器の値を更新するタイミングになります。"
        )}
        {t("GHS-8ATではロギングしていない状態では1秒に1回,") +
          t("ロギング中は2秒に1回測定を行っています。")}
        {t(
          "この1秒と2秒の違いの理由はロギング中では消費電流を抑えるため間隔を長く2秒,"
        ) +
          t(
            "ロギングしていない状態ではゼロ調整やスパン校正をマニュアル操作で行うため間隔を短く1秒としています。"
          )}
        {t(
          "この測定間隔は全てのセンサフルスケールについて同じ仕様になっています。"
        )}
      </p>

      <h3 className="p-heading-3">
        {t("センサフルスケールによる測定値の扱い")}
      </h3>
      <p className="c-text">
        {t(
          "例として下図のグラフに示すようにガス濃度が変化した場合フルスケール100ppm,500ppm,1000ppmのセンサで測定値はどのように扱われるか示しました。"
        )}
        {t("下図1内下表に各フルスケールでの測定結果が示されています。")}
        {t(
          "1000ppmセンサは経過時間0～26秒の間は最小分解能の10ppmを超えないため0ppmという測定値になります。"
        )}
        {t("このように最小分解能より小さい値は低い値側へ丸めて扱われます。")}
      </p>
      <img
        src={currentImages.Ghs8atMeasureConc}
        alt={t("GHS-8AT測定値の扱い")}
      />
      <p>
        {t("図1.ガス濃度とGHS-8ATのセンサフルスケールの違いによる測定結果")}
      </p>

      <h3 className="p-heading-3">
        {t("センサフルスケールによる分解能の違い")}
      </h3>
      <p className="c-text">
        {t("センサフルスケールによる最小分解能の違いは以下表になります。")}
      </p>

      <Table headers={translatedResolutionHeaders} data={resolutions} />
      <p>
        {t("表1.センサフルスケールと最小分解能の関係")}
        <br />
        {t("※製造年月2013年3月以降のセンサは5ppmから1ppmに変更されました")}
      </p>

      <h3 className="p-heading-3">{t("各ロギング条件の動作")}</h3>
      <p className="c-text">
        {t(
          "ロギング条件は瞬時(REAL),最小(MIN),最大(MAX),平均(AVE)の4種類があります。"
        )}
        {t(
          "例として下図2にガス濃度がグラフに示すように変化した場合各ロギング条件での測定結果を示します。"
        )}
        {t("この例ではセンサフルスケール100ppmロギング間隔は1分としています。")}
      </p>
      <img
        src={currentImages.Ghs8atLogCon100}
        alt={t("GHS-8ATロギング条件の動作")}
      />
      <p>{t("図2.ロギング条件によるロギングデータの違い")}</p>

      <h3 className="p-heading-3">
        {t("ロギング条件を平均とした場合のデータ算出ついて")}
      </h3>
      <p className="c-text">
        {t("平均値はロギング間隔にある全データの平均値を算出しています。")}
        {t("測定間隔は2秒なので例えば1分ロギングの場合には30データ,") +
          t("5分ロギングでは150データの平均を算出しています。")}
        {t(
          "前述図2の場合には経過時間2秒から60秒までの30個の測定データを合計した後30で除算しています。"
        )}
        {t(
          "2秒から60秒までの合計は251になりこれを30で除算すると8.3667となりますがロギングデータとしては小数点以下を切り捨て8[ppm]として保存されます。"
        )}
        {t(
          "各センサフルスケールにより平均値算出時の値切り捨ては以下表のようにフルスケール10ppm以外は全て小数点以下の切り捨てになります。"
        )}
      </p>
      <Table headers={translatedAverageTableHeader} data={averageTableData} />
      <p>{t("表2.ロギング条件平均での平均値算出結果の扱いについて")}</p>
    </section>
  );
};
